export const SYSTOLIC_BLOOD_PRESSURE = 'SYSTOLIC_BLOOD_PRESSURE';
export const DIASTOLIC_BLOOD_PRESSURE = 'DIASTOLIC_BLOOD_PRESSURE';
export const BLOOD_PRESSURE_DISPLAY_NAME = 'Blood pressure';

export const getMeasurementId = (measurement, index) =>
  measurement.measurementId || `measurement_${index}`;

export const isBloodPressurePair = (row) => row?.kind === 'pair';

export const getDisplayRowId = (row) =>
  isBloodPressurePair(row) ? `bp:${row.systolic.id}::${row.diastolic.id}` : row.id;

export const getDisplayRowIds = (row) =>
  isBloodPressurePair(row) ? [row.systolic.id, row.diastolic.id] : [row.id];

export const isDisplayRowSelected = (row) => {
  if (isBloodPressurePair(row)) {
    return !!(row.systolic.measurement.isSelected && row.diastolic.measurement.isSelected);
  }
  return !!row.measurement.isSelected;
};

export const getDisplayRowDate = (row) => {
  if (isBloodPressurePair(row)) {
    return row.systolic.measurement.date || row.diastolic.measurement.date || null;
  }
  return row.measurement.date || null;
};

export const getDisplayRowUnit = (row) => {
  if (isBloodPressurePair(row)) {
    return row.systolic.measurement.unit || row.diastolic.measurement.unit || '';
  }
  return row.measurement.unit || '';
};

export const formatBloodPressureReading = (systolicMeasurement, diastolicMeasurement) => {
  const unit = systolicMeasurement?.unit || diastolicMeasurement?.unit || '';
  const systolic = systolicMeasurement?.value ?? '';
  const diastolic = diastolicMeasurement?.value ?? '';
  const reading = `${systolic} / ${diastolic}`;
  return unit ? `${reading} ${unit}` : reading;
};

/**
 * Pairs SYSTOLIC_BLOOD_PRESSURE and DIASTOLIC_BLOOD_PRESSURE by position when counts match,
 * matching EMIS AssignBloodPressureGroupIds. Returns display rows of kind 'single' or 'pair'.
 */
export const bundleBloodPressureReadings = (measurements, shouldBundle) => {
  if (!measurements?.length) {
    return [];
  }

  const entries = measurements.map((measurement, index) => ({
    kind: 'single',
    measurement,
    index,
    id: getMeasurementId(measurement, index),
  }));

  if (!shouldBundle) {
    return entries;
  }

  const systolicEntries = entries.filter((entry) => entry.measurement.type === SYSTOLIC_BLOOD_PRESSURE);
  const diastolicEntries = entries.filter((entry) => entry.measurement.type === DIASTOLIC_BLOOD_PRESSURE);

  if (
    systolicEntries.length === 0 ||
    diastolicEntries.length === 0 ||
    systolicEntries.length !== diastolicEntries.length
  ) {
    return entries;
  }

  const pairById = new Map();
  for (let pairIndex = 0; pairIndex < systolicEntries.length; pairIndex += 1) {
    const pair = {
      kind: 'pair',
      systolic: systolicEntries[pairIndex],
      diastolic: diastolicEntries[pairIndex],
    };
    pairById.set(systolicEntries[pairIndex].id, pair);
    pairById.set(diastolicEntries[pairIndex].id, pair);
  }

  const emitted = new Set();
  const rows = [];
  entries.forEach((entry) => {
    const pair = pairById.get(entry.id);
    if (!pair) {
      rows.push(entry);
      return;
    }
    const pairId = getDisplayRowId(pair);
    if (emitted.has(pairId)) {
      return;
    }
    emitted.add(pairId);
    rows.push(pair);
  });

  return rows;
};
